import { useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { Alignment, Fit, Layout, useStateMachineInput } from '@rive-app/react-canvas';
import classNames from 'classnames';
import { observer } from 'mobx-react';

import { RIVE_DEFAULTS } from '../../../../hooks/rive/constants';
import { useRiveMachine } from '../../../../hooks/rive/useRiveMachine';
import { EnumOpenDialog } from '../../../../stores/ui/types';
import { useStores } from '../../../../stores/useStores';

import { guardRepeatClicks } from './bannerCtaAction';
import styles from './RiveHeader.module.scss';
import { bindTocLobbyBanner, type TocLobbyBannerValues } from './tocLobbyBanner';

interface TocLobbyHeaderProps {
  riveUrl: string;
  values: TocLobbyBannerValues | null;
  bannerClassName?: string;
  /** `riveUrl` once its file has loaded, null once it leaves. */
  onShown?: (riveUrl: string | null) => void;
}

/** The ToC lobby file in place of the lobby banner; on SC a tap opens the ToC dialog. Key it by `riveUrl`. */
const TocLobbyHeader: React.FC<TocLobbyHeaderProps> = observer(({ riveUrl, values, bannerClassName, onShown }) => {
  const { uiStore, tournamentStore, tournamentOfChampionsStore, coinTypeStore } = useStores();
  const layout = useMemo(() => new Layout({ fit: Fit.Cover, alignment: Alignment.Center }), []);
  const [hasLoaded, setHasLoaded] = useState(false);
  const { rive, RiveComponent } = useRiveMachine({
    src: riveUrl,
    autoBind: true,
    isTouchScrollEnabled: true,
    layout,
    onLoad: () => setHasLoaded(true),
    onLoadError: () => uiStore.markTocLobbyRiveFailed(riveUrl),
  });

  useEffect(() => {
    if (!hasLoaded || !onShown) return;
    onShown(riveUrl);
    return () => onShown(null);
  }, [hasLoaded, onShown, riveUrl]);

  useEffect(() => {
    const vm = rive?.viewModelInstance;
    if (!vm || !values) return;
    bindTocLobbyBanner(vm, values);
  }, [rive, values]);

  const isScInput = useStateMachineInput(rive, RIVE_DEFAULTS.STATE_MACHINE_NAME, 'is_sc');
  const isSc = coinTypeStore.coinType === 'SC';
  useEffect(() => {
    if (isScInput) isScInput.value = isSc;
  }, [isScInput, isSc]);

  const onTap = useMemo(
    () =>
      guardRepeatClicks(() => {
        void tournamentStore.fetch();
        if (!tournamentOfChampionsStore.canOpenDialog) return;
        uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenTournamentDialog, options: { shown_by: 'click' } });
      }),
    [tournamentStore, tournamentOfChampionsStore, uiStore],
  );
  const isTappable = isSc && tournamentOfChampionsStore.canOpenDialog;
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onTap();
  };

  return (
    <div
      className={classNames(styles.tocLobby, { [styles.tocLobbyInteractive]: isTappable }, bannerClassName)}
      data-testid="toc-lobby-banner"
      role={isTappable ? 'button' : undefined}
      tabIndex={isTappable ? 0 : undefined}
      onClick={isTappable ? onTap : undefined}
      onKeyDown={isTappable ? onKeyDown : undefined}
    >
      <RiveComponent className={styles.tocLobbyRive} />
    </div>
  );
});

export default TocLobbyHeader;
